import { getPolygonPrimaryRpcUrl } from '@/utils/polygon-rpc';

/** keccak256("Transfer(address,address,uint256)") — 必须 32 字节（64 hex） */
const TRANSFER_TOPIC = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';
export const DEPOSIT_WATCH_SESSION_KEY = 'deposit-chain-watch-v2';

export const POLL_PHASE = {
  SLOW: 'slow',
  FAST: 'fast',
  TAIL: 'tail',
};

export const POLL_MS = {
  slow: 25_000,
  hidden: 45_000,
  fast: 2_500,
  tail: 15_000,
};

/** 非 persistent 模式：tail 空闲后停止 */
export const TAIL_IDLE_STOP_MS = 5 * 60 * 1000;
/** 非 persistent 模式：总监听时长上限 */
export const WATCH_TIMEOUT_MS = 30 * 60 * 1000;
/** persistent 模式：tail 空闲后回到 slow 继续扫 */
export const TAIL_IDLE_RETURN_SLOW_MS = 5 * 60 * 1000;
export const DEFAULT_FROM_BLOCK_LOOKBACK = 80;
/** 每轮 getLogs 最大块跨度（Polygon ~2s/块，400≈13min） */
export const SLIDING_BLOCK_WINDOW = 400;
const BLOCK_SCAN_OVERLAP = 2n;
const RPC_TIMEOUT_MS = 8000;

function normalizeAddress(value) {
  return String(value ?? '').trim();
}

function padAddressTopic(address) {
  const hex = normalizeAddress(address).toLowerCase().replace(/^0x/, '');
  if (hex.length !== 40) {
    console.warn('[deposit-monitor] invalid address for topic', address, hex.length);
    return '';
  }
  return `0x${'0'.repeat(24)}${hex}`;
}

/** ERC20 Transfer log topics[1|2] → 0x 地址 */
export function parseTransferLogAddress(topic) {
  const raw = String(topic ?? '')
    .trim()
    .toLowerCase();
  if (!raw.startsWith('0x') || raw.length < 42) return '';
  return `0x${raw.slice(-40)}`;
}

function toHexBlock(value) {
  if (value == null) return undefined;
  if (typeof value === 'string' && value.startsWith('0x')) return value;
  const n = BigInt(value);
  return `0x${n.toString(16)}`;
}

function parseBlockHex(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && value.startsWith('0x')) return BigInt(value);
  if (typeof value === 'number' && Number.isFinite(value))
    return BigInt(Math.max(0, Math.floor(value)));
  return null;
}

/**
 * 滑动窗口计算本轮 fromBlock，避免长时间挂着后 getLogs 块范围过大
 */
export function resolveTickScanRange(
  latestHex,
  { anchorFromBlock, lastScannedBlock, windowBlocks } = {}
) {
  const latest = parseBlockHex(latestHex);
  if (latest == null) {
    return { fromBlock: toHexBlock(anchorFromBlock) || 'latest', toBlock: 'latest' };
  }

  const window = BigInt(Math.max(1, Number(windowBlocks) || SLIDING_BLOCK_WINDOW));
  const byWindow = latest > window ? latest - window : 0n;

  let from = byWindow;
  const last = parseBlockHex(lastScannedBlock);
  if (last != null) {
    const byLast = last > BLOCK_SCAN_OVERLAP ? last - BLOCK_SCAN_OVERLAP : 0n;
    from = byLast > byWindow ? byLast : byWindow;
  } else {
    const anchor = parseBlockHex(anchorFromBlock);
    if (anchor != null && anchor > from) {
      from = anchor;
    }
  }

  return {
    fromBlock: `0x${from.toString(16)}`,
    toBlock: `0x${latest.toString(16)}`,
    latestBlock: `0x${latest.toString(16)}`,
  };
}

export function resolveDepositRpcUrl(chainId) {
  const id = Number(chainId);
  if (!Number.isFinite(id) || id <= 0 || id === 137) {
    return getPolygonPrimaryRpcUrl();
  }
  console.warn('[deposit-monitor] chain RPC not configured, falling back to Polygon RPC', id);
  return getPolygonPrimaryRpcUrl();
}

async function jsonRpc(rpcUrl, method, params, timeoutMs = RPC_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`rpc ${res.status}`);
    const json = await res.json();
    if (json.error) throw new Error(json.error.message || `${method} failed`);
    return json.result;
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchBlockNumber(rpcUrl) {
  const hex = await jsonRpc(rpcUrl, 'eth_blockNumber', []);
  return typeof hex === 'string' ? hex : '0x0';
}

/** 从当前块往前回溯，避免「先转账后开弹窗」漏单 */
export async function fetchFromBlockWithLookback(rpcUrl, lookback = DEFAULT_FROM_BLOCK_LOOKBACK) {
  const latestHex = await fetchBlockNumber(rpcUrl);
  const latest = BigInt(latestHex);
  const safeLookback = BigInt(Math.max(0, Number(lookback) || 0));
  const from = latest > safeLookback ? latest - safeLookback : 0n;
  return `0x${from.toString(16)}`;
}

export async function fetchIncomingTransferLogsMulti(
  rpcUrl,
  { tokenContracts, watchAddress, fromBlock, toBlock }
) {
  const contracts = [
    ...new Set(
      (Array.isArray(tokenContracts) ? tokenContracts : [])
        .map((item) => normalizeAddress(item))
        .filter(Boolean)
    ),
  ];
  if (!contracts.length) return [];

  const batches = await Promise.all(
    contracts.map((tokenContract) =>
      fetchIncomingTransferLogs(rpcUrl, { tokenContract, watchAddress, fromBlock, toBlock }).catch(
        (err) => {
          console.warn('[deposit-monitor] getLogs failed', tokenContract, err);
          return [];
        }
      )
    )
  );
  return batches.flat();
}

export async function fetchTransactionReceipt(rpcUrl, txHash) {
  const hash = normalizeAddress(txHash);
  if (!hash) return null;
  const receipt = await jsonRpc(rpcUrl, 'eth_getTransactionReceipt', [hash]);
  return receipt || null;
}

/** 区块时间 → ms；失败返回 null */
export async function fetchBlockTimestampMs(rpcUrl, blockNumber) {
  if (blockNumber == null || blockNumber === '') return null;
  try {
    const block = await jsonRpc(rpcUrl, 'eth_getBlockByNumber', [toHexBlock(blockNumber), false]);
    const raw = block?.timestamp;
    if (raw == null || raw === '') return null;
    const sec = typeof raw === 'string' && raw.startsWith('0x') ? Number(BigInt(raw)) : Number(raw);
    if (!Number.isFinite(sec) || sec <= 0) return null;
    return Math.floor(sec * 1000);
  } catch (err) {
    console.warn('[deposit-monitor] fetch block timestamp failed', err);
    return null;
  }
}

export async function resolveReceiptBlockTimeMs(rpcUrl, receipt) {
  if (!receipt?.blockNumber) return null;
  return fetchBlockTimestampMs(rpcUrl, receipt.blockNumber);
}

/** 根据 txHash 查 receipt + 出块时间 */
export async function resolveTxBlockTimeMs(rpcUrl, txHash) {
  const receipt = await fetchTransactionReceipt(rpcUrl, txHash);
  if (!receipt || !isReceiptSuccess(receipt)) return null;
  return resolveReceiptBlockTimeMs(rpcUrl, receipt);
}

export async function fetchIncomingTransferLogs(
  rpcUrl,
  { tokenContract, watchAddress, fromBlock, toBlock }
) {
  const contract = normalizeAddress(tokenContract);
  const watch = normalizeAddress(watchAddress);
  if (!contract || !watch) return [];

  const params = [
    {
      address: contract,
      fromBlock: toHexBlock(fromBlock) || 'latest',
      toBlock: toHexBlock(toBlock) || 'latest',
      topics: [TRANSFER_TOPIC, null, padAddressTopic(watch)],
    },
  ];
  const logs = await jsonRpc(rpcUrl, 'eth_getLogs', params);
  return Array.isArray(logs) ? logs : [];
}

export function parseTransferAmount(dataHex, decimals = 6) {
  if (!dataHex || dataHex === '0x') return 0;
  try {
    const raw = BigInt(dataHex);
    const dec = Number(decimals);
    const safeDec = Number.isFinite(dec) && dec >= 0 ? dec : 6;
    const amount = Number(raw) / 10 ** safeDec;
    return Number.isFinite(amount) ? amount : 0;
  } catch {
    return 0;
  }
}

function isReceiptSuccess(receipt) {
  const status = String(receipt?.status ?? '').toLowerCase();
  return status === '0x1' || status === '1';
}

function isReceiptFailed(receipt) {
  const status = String(receipt?.status ?? '').toLowerCase();
  return status === '0x0' || status === '0';
}

export { isReceiptSuccess, isReceiptFailed };

function resolvePollMs(phase, options = {}) {
  const base = POLL_MS[phase] ?? POLL_MS.slow;
  if (
    options.slowOnHidden &&
    phase === POLL_PHASE.SLOW &&
    typeof document !== 'undefined' &&
    document.visibilityState === 'hidden'
  ) {
    return POLL_MS.hidden ?? base;
  }
  return base;
}

const VALID_POLL_PHASES = new Set(Object.values(POLL_PHASE));

/**
 * 自适应频率监听转入 watchAddress 的 ERC20 Transfer
 * slow → 检测到 tx → fast → 确认成功 → tail →（persistent：回 slow 常驻 / 否则 idle 停止）
 * @returns {{ stop: () => void, getState: () => object, setPhase: Function }}
 */
export function pollIncomingTransfer(options = {}) {
  const rpcUrl = normalizeAddress(options.rpcUrl);
  const watchAddress = normalizeAddress(options.watchAddress);
  const tokenContracts = [
    ...new Set(
      [
        ...(Array.isArray(options.tokenContracts) ? options.tokenContracts : []),
        options.tokenContract,
      ]
        .map((item) => normalizeAddress(item))
        .filter(Boolean)
    ),
  ];
  const decimals = options.decimals ?? 6;
  const anchorFromBlock = options.fromBlock;
  const persistent = Boolean(options.persistent);
  const slidingWindow = options.slidingWindow ?? SLIDING_BLOCK_WINDOW;

  if (!rpcUrl || !watchAddress || !tokenContracts.length) {
    return { stop: () => {}, getState: () => ({}), setPhase: () => {} };
  }

  let stopped = false;
  let timerId = null;
  let ticking = false;
  let pendingKick = false;
  let phase = options.initialPhase ?? POLL_PHASE.SLOW;
  let lastActivityAt = options.lastActivityAt ?? Date.now();
  let lastScannedBlock = options.lastScannedBlock ?? null;
  const seenConfirmed = new Set(Array.isArray(options.seenTx) ? options.seenTx : []);
  const pendingTx = new Map();
  const startedAt = Date.now();

  async function processPendingReceipts() {
    for (const [txHash, meta] of [...pendingTx.entries()]) {
      if (stopped) return;
      const receipt = await fetchTransactionReceipt(rpcUrl, txHash);
      if (stopped) return;
      if (!receipt) continue;

      pendingTx.delete(txHash);
      seenConfirmed.add(txHash);
      lastActivityAt = Date.now();

      if (isReceiptSuccess(receipt)) {
        phase = POLL_PHASE.TAIL;
        if (!stopped) options.onPhaseChange?.(phase, lastActivityAt);
        if (!stopped) {
          const blockTimestampMs = await resolveReceiptBlockTimeMs(rpcUrl, receipt);
          if (stopped) return;
          options.onConfirmed?.({
            txHash,
            amount: meta.amount,
            fromAddress: meta.fromAddress,
            blockTimestampMs,
          });
        }
      } else if (isReceiptFailed(receipt)) {
        phase = POLL_PHASE.TAIL;
        if (!stopped) options.onPhaseChange?.(phase, lastActivityAt);
        if (!stopped) {
          options.onFailed?.({ txHash, amount: meta.amount, fromAddress: meta.fromAddress });
        }
      }
    }
  }

  async function tick() {
    if (stopped || ticking) return;
    ticking = true;

    try {
      if (!persistent && Date.now() - startedAt > WATCH_TIMEOUT_MS) {
        if (!stopped) options.onTimeout?.();
        stop();
        return;
      }

      if (phase === POLL_PHASE.TAIL) {
        const tailIdleMs = persistent ? TAIL_IDLE_RETURN_SLOW_MS : TAIL_IDLE_STOP_MS;
        if (Date.now() - lastActivityAt > tailIdleMs) {
          if (persistent) {
            phase = POLL_PHASE.SLOW;
            if (!stopped) options.onPhaseChange?.(phase, lastActivityAt);
            if (!stopped) options.onReturnToSlow?.();
          } else {
            if (!stopped) options.onIdleStop?.();
            stop();
          }
          return;
        }
      }

      try {
        await processPendingReceipts();
        if (stopped) return;

        const latestHex = await fetchBlockNumber(rpcUrl);
        if (stopped) return;

        const scanRange = resolveTickScanRange(latestHex, {
          anchorFromBlock,
          lastScannedBlock,
          windowBlocks: slidingWindow,
        });

        const logs = await fetchIncomingTransferLogsMulti(rpcUrl, {
          tokenContracts,
          watchAddress,
          fromBlock: scanRange.fromBlock,
          toBlock: scanRange.toBlock,
        });
        if (stopped) return;

        if (scanRange.latestBlock) {
          lastScannedBlock = scanRange.latestBlock;
          options.onBlockScanned?.(scanRange.latestBlock);
        }

        const newlyDetected = [];
        for (const log of logs) {
          const txHash = normalizeAddress(log?.transactionHash);
          if (!txHash || seenConfirmed.has(txHash) || pendingTx.has(txHash)) continue;

          const amount = parseTransferAmount(log?.data, decimals);
          const fromAddress = parseTransferLogAddress(log?.topics?.[1]);
          pendingTx.set(txHash, { amount, fromAddress });
          lastActivityAt = Date.now();
          newlyDetected.push({ txHash, amount, fromAddress });
        }

        if (newlyDetected.length && !stopped) {
          phase = POLL_PHASE.FAST;
          options.onPhaseChange?.(phase, lastActivityAt);
          if (typeof options.onDetectedBatch === 'function') {
            options.onDetectedBatch(newlyDetected);
          } else {
            for (const detected of newlyDetected) {
              options.onDetected?.(detected);
            }
          }
        }

        await processPendingReceipts();
      } catch (err) {
        console.warn('[deposit-monitor] incoming tick failed', err);
      }
    } finally {
      ticking = false;
      if (!stopped) {
        try {
          options.onTick?.({ phase, lastActivityAt });
        } catch (err) {
          console.warn('[deposit-monitor] onTick failed', err);
        }
      }
      if (!stopped && pendingKick) {
        pendingKick = false;
        void tick().then(() => {
          if (!stopped) scheduleNext();
        });
      }
    }
  }

  function scheduleNext() {
    if (stopped || ticking || pendingKick) return;
    clearTimeout(timerId);
    timerId = setTimeout(async () => {
      await tick();
      if (!stopped && !pendingKick) scheduleNext();
    }, resolvePollMs(phase, { slowOnHidden: persistent }));
  }

  function setPhase(nextPhase, { kick = false } = {}) {
    if (stopped) return;
    if (!VALID_POLL_PHASES.has(nextPhase)) return;
    const prev = phase;
    phase = nextPhase;
    if (prev !== nextPhase) {
      options.onPhaseChange?.(phase, lastActivityAt);
    }
    if (!kick) {
      // tick 进行中：只改 phase，由当前 tick 结束后的 scheduleNext 接新间隔
      if (!ticking) scheduleNext();
      return;
    }
    clearTimeout(timerId);
    timerId = null;
    if (ticking) {
      pendingKick = true;
      return;
    }
    void tick().then(() => {
      if (!stopped && !pendingKick) scheduleNext();
    });
  }

  function stop() {
    stopped = true;
    pendingKick = false;
    if (timerId != null) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  tick().then(() => {
    if (!stopped) scheduleNext();
  });

  return {
    stop,
    setPhase,
    markSeen(txHash) {
      const hash = String(txHash ?? '')
        .trim()
        .toLowerCase();
      if (hash) seenConfirmed.add(hash);
    },
    getState: () => ({
      phase,
      lastActivityAt,
      seenTx: [...seenConfirmed],
      lastScannedBlock,
      busy: ticking,
    }),
  };
}

/**
 * 轮询 txHash receipt（connected 钱包），确认后由上层切入 tail 入账监听
 * @returns {{ stop: () => void }}
 */
export function pollTxReceipt(options = {}) {
  const rpcUrl = normalizeAddress(options.rpcUrl);
  const txHash = normalizeAddress(options.txHash);

  if (!rpcUrl || !txHash) {
    return { stop: () => {} };
  }

  let stopped = false;
  let timerId = null;
  const startedAt = Date.now();

  async function tick() {
    if (stopped) return;
    if (Date.now() - startedAt > WATCH_TIMEOUT_MS) {
      if (!stopped) options.onTimeout?.();
      stop();
      return;
    }

    try {
      const receipt = await fetchTransactionReceipt(rpcUrl, txHash);
      if (stopped) return;
      if (!receipt) return;

      if (isReceiptSuccess(receipt)) {
        const blockTimestampMs = await resolveReceiptBlockTimeMs(rpcUrl, receipt);
        if (stopped) return;
        if (!stopped) options.onConfirmed?.({ txHash, blockTimestampMs });
        stop();
        return;
      }
      if (isReceiptFailed(receipt)) {
        if (!stopped) options.onFailed?.({ txHash });
        stop();
      }
    } catch (err) {
      console.warn('[deposit-monitor] receipt tick failed', err);
    }
  }

  function scheduleNext() {
    if (stopped) return;
    clearTimeout(timerId);
    timerId = setTimeout(async () => {
      await tick();
      if (!stopped) scheduleNext();
    }, POLL_MS.fast);
  }

  function stop() {
    stopped = true;
    if (timerId != null) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  tick().then(() => {
    if (!stopped) scheduleNext();
  });
  return { stop };
}

export function loadDepositWatchSession() {
  try {
    const raw = sessionStorage.getItem(DEPOSIT_WATCH_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function saveDepositWatchSession(session) {
  try {
    if (!session) {
      sessionStorage.removeItem(DEPOSIT_WATCH_SESSION_KEY);
      return;
    }
    sessionStorage.setItem(DEPOSIT_WATCH_SESSION_KEY, JSON.stringify(session));
  } catch {
    // ignore quota / private mode
  }
}

export function clearDepositWatchSession() {
  saveDepositWatchSession(null);
}
